import { clsx } from "clsx";
import React, { useCallback, useRef } from "react";
import AppFooter from "web/react/components/app-footer/app-footer";
import { CustomerCaptureOverlay } from "web/react/components/customer-capture-overlay/customer-capture-overlay";
import { Divider } from "web/react/components/divider";
import { FooterProps } from "web/react/components/footer/footer";
import { LeadLink } from "web/react/components/lead-link";
import { RelatedRail } from "web/react/components/related-rail/related-rail";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import { InfoCallout } from "web/react/emo/info-callout";
import { Text } from "web/react/emo/text";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import {
    ProductBuyOptionsSerializer,
    ProductLayoutSerializer,
    ProductPageAreaSerializer,
} from "web/types/serializers";
import * as styles from "./out-of-stock-product-page.css";
import { ProductArea } from "./product-area";

const getInfoCalloutTitle = (
    buyOptions: ProductBuyOptionsSerializer[],
    uniqueRetailerCount: number,
    isCheckout: boolean
): string => {
    if (!buyOptions?.length) return gettext("oos_product.info_callout_no_retailers");
    else if (isCheckout) return gettext("oos_product.info_callout_icon");
    else {
        return uniqueRetailerCount === 2
            ? gettext("oos_product.info_callout_affiliate_two_retailers", {
                  retailer_name: buyOptions[0].retailer_name,
              })
            : uniqueRetailerCount > 2
            ? gettext("oos_product.info_callout_affiliate_multiple_retailers", {
                  retailer_name: buyOptions[0].retailer_name,
                  retailer_count: uniqueRetailerCount - 1,
              })
            : gettext("oos_product.info_callout_affiliate", {
                  retailer_name: buyOptions[0].retailer_name,
              });
    }
};

// remove me as the page is built
const TempSection = ({
    title,
    style,
}: {
    title: string;
    style?: React.CSSProperties;
}): React.ReactElement => {
    return (
        <div className={styles.tempSectionContainer} style={style}>
            {title}
        </div>
    );
};

function SectionDivider({ isMobileOnly }: { isMobileOnly?: boolean }): React.ReactElement {
    return (
        <div className={clsx({ [styles.hideDesktop]: isMobileOnly })}>
            <Divider />
        </div>
    );
}

export interface OutOfStockProductPageProps {
    productArea: ProductPageAreaSerializer;
    productLayout: ProductLayoutSerializer;
    mainBuyOption: ProductBuyOptionsSerializer;
    isCheckout: boolean;
    isOverlay?: boolean;
    parentRef?: React.RefObject<HTMLElement>;
    footerData?: FooterProps;
}
export function OutOfStockProductPage({
    productArea,
    productLayout,
    isOverlay,
    isCheckout,
    mainBuyOption,
    // parentRef,
    footerData,
}: OutOfStockProductPageProps): React.ReactElement {
    const uniqueRetailerCount = productArea.unique_retailers.length;
    const productDetailsContainerRef = useRef<HTMLDivElement>(null);

    const eventCustomData = {
        buy_option_id: mainBuyOption.link_id.toString(),
        product_id: productArea.product.product_id,
        retailer_slug: mainBuyOption.retailer_slug,
    };

    const sendAnalyticsEvent = useCallback((action, category, label, customData) => {
        analytics.event(
            category,
            action,
            label,
            action !== "click", // nonInteraction
            customData, // customData
            "", // subType
            {}, // checkoutData
            [] // itemData
        );
    }, []);

    return (
        <>
            <div className={styles.pageWrapper}>
                {!isOverlay && <CustomerCaptureOverlay />}
                <div
                    className={clsx(
                        styles.pageContent,
                        styles.pageContentPadding[isOverlay ? "overlay" : "standard"]
                    )}
                >
                    <div className={styles.heroSection}>
                        <InfoCallout
                            variant="information"
                            title={getInfoCalloutTitle(
                                productArea?.buy_options,
                                uniqueRetailerCount,
                                isCheckout
                            )}
                        />
                        {!isCheckout && mainBuyOption.last_seen_in_stock && (
                            <div className={styles.lastSeenTimeContainer}>
                                <LeadLink
                                    href={mainBuyOption.affiliate_url}
                                    reason="last-seen-time-link"
                                    rel="noopener noreferrer"
                                    className={styles.lastSeenLink}
                                    productId={productArea.product.product_id}
                                    openInNewTab
                                    eventAction="click"
                                    eventCategory="oos-product-hero"
                                    eventLabel={"affiliate-buy-on-store-last-seen-link"}
                                    eventCustomData={eventCustomData}
                                >
                                    <Text textStyle={"body-bold"} as="span">
                                        {mainBuyOption.last_seen_in_stock}
                                        <SVGIcon
                                            name="external-link"
                                            overrideStyle
                                            className={styles.lastSeenLinkIcon}
                                        />
                                    </Text>
                                </LeadLink>
                            </div>
                        )}
                        {isCheckout && mainBuyOption.last_seen_in_stock && (
                            <div className={styles.lastSeenTimeContainer}>
                                <a
                                    href={mainBuyOption.retailer_link}
                                    rel="noopener noreferrer"
                                    className={styles.lastSeenLink}
                                    onClick={() =>
                                        sendAnalyticsEvent(
                                            "click",
                                            "oos-product-hero",
                                            "checkout-last-seen-link",
                                            eventCustomData
                                        )
                                    }
                                >
                                    <Text textStyle={"body-bold"} as="span">
                                        {mainBuyOption.last_seen_in_stock}
                                    </Text>
                                </a>
                            </div>
                        )}
                        <div className={styles.mainBuyOptionPlusOtherOptionsContainer}>
                            <ProductArea
                                productArea={productArea}
                                productLayout={productLayout}
                                uniqueRetailerCount={uniqueRetailerCount}
                                isCheckout={isCheckout}
                                mainBuyOption={mainBuyOption}
                                productDetailsContainerRef={productDetailsContainerRef}
                            />
                            <TempSection
                                style={{ width: "427px" }}
                                title={"previously available"}
                            />
                        </div>
                    </div>
                    <SectionDivider />
                    <TempSection title={"pba"} />
                    <TempSection title={"more like this grid"} />
                    {productLayout.related_designers_rail?.candidates?.length && (
                        <RelatedRail
                            title={productLayout.related_designers_rail.title}
                            candidates={productLayout.related_designers_rail.candidates}
                            analyticsLabel="designers"
                        />
                    )}
                    <SectionDivider isMobileOnly />
                    <div ref={productDetailsContainerRef} id="product-details">
                        <TempSection title={"full product details"} />
                    </div>
                    <TempSection title={"people also searched for"} />
                    <SectionDivider isMobileOnly />
                    <TempSection title={"recently viewed"} />
                </div>
            </div>
            {footerData && (
                <AppFooter
                    data={footerData.data}
                    helpCentreURLs={footerData.helpCentreURLs}
                    country={footerData.country}
                />
            )}
        </>
    );
}
